import type { ILiveBrowserState, ILiveVideoSource } from '@push.rocks/smartpuppeteer';
import type { ILiveBrowserCanvasError, ILiveBrowserCanvasOperationOptions } from './interfaces.livebrowsercanvas.js';
import type { ILiveBrowserVideoClient, ILiveBrowserVideoStatistics } from './interfaces.livebrowservideo.js';

interface IVideoReceiverOptions {
  video: HTMLVideoElement;
  client: ILiveBrowserVideoClient;
  operationTimeoutMs: number;
  onUnavailable(): void;
  onPresented(sourceArg: ILiveVideoSource): void;
  onError(errorArg: ILiveBrowserCanvasError): void;
}

const viewportKey = (sourceArg: ILiveVideoSource): string => JSON.stringify([
  sourceArg.tabId, sourceArg.viewportRevision,
  sourceArg.viewport.width, sourceArg.viewport.height, sourceArg.viewport.deviceScaleFactor,
]);

const sourceKey = (source: ILiveVideoSource): string => source.tabId;
const presentationKey = (source: ILiveVideoSource): string => JSON.stringify([
  viewportKey(source), source.generation, source.frameAlignment, source.rtpTimestampFloor,
]);
const intervalDelay = (current: number | undefined, previous: number | undefined, count: number): number | undefined => (
  current === undefined || previous === undefined || count <= 0 ? undefined : (current - previous) * 1000 / count
);

/** One native receiver. Every replacement closes the previous host peer before opening another. */
export class LiveBrowserVideoReceiver {
  private epoch = 0;
  private desiredKey?: string;
  private currentSource?: ILiveVideoSource;
  private presentedSourceKey?: string;
  private presentationNotBefore = 0;
  private controller?: AbortController;
  private peer?: RTCPeerConnection;
  private frameCallback?: number;
  private connectionTimer?: ReturnType<typeof setTimeout>;
  private sampleTimer?: ReturnType<typeof setTimeout>;
  private senderSampleTimer?: ReturnType<typeof setTimeout>;
  private framesPresented = 0;
  private lastPresentedCount?: number;
  private lastCaptureTime?: number;
  private tail: Promise<void> = Promise.resolve();
  private remoteMayExist = false;
  private statistics?: ILiveBrowserVideoStatistics;
  private decodedBeforePeer = 0;
  private droppedBeforePeer = 0;
  private readonly geometryObserver: ResizeObserver;
  private geometrySource?: ILiveVideoSource;
  private contentSize?: { width: number; height: number };

  constructor(private readonly options: IVideoReceiverOptions) {
    const video = options.video;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.style.objectFit = 'contain';
    this.geometryObserver = new ResizeObserver(entries => {
      const size = entries[0]?.contentRect;
      if (!size) return;
      this.contentSize = { width: size.width, height: size.height };
      this.applyGeometry();
    });
  }

  /** Visible source rectangle, shared by GPU presentation and pointer mapping. */
  public getContentBounds(): { left: number; top: number; width: number; height: number } {
    const bounds = this.options.video.getBoundingClientRect();
    const source = this.geometrySource;
    if (!source) return bounds;
    const { width, height } = source.viewport;
    if (source.frameAlignment === 2) return {
      left: bounds.left, top: bounds.top,
      width: bounds.width * width / (Math.ceil(width / 2) * 2),
      height: bounds.height * height / (Math.ceil(height / 2) * 2),
    };
    const scale = Math.min(bounds.width / width, bounds.height / height);
    return { left: bounds.left + (bounds.width - width * scale) / 2,
      top: bounds.top + (bounds.height - height * scale) / 2, width: width * scale, height: height * scale };
  }

  private applyGeometry(): void {
    const video = this.options.video;
    const source = this.geometrySource;
    if (source?.frameAlignment !== 2) {
      video.style.objectFit = 'contain';
      video.style.removeProperty('transform');
      video.style.removeProperty('transform-origin');
      video.style.removeProperty('clip-path');
      return;
    }
    const box = this.contentSize;
    if (!box?.width || !box.height) return;
    const { width, height } = source.viewport;
    const codedWidth = Math.ceil(width / 2) * 2;
    const codedHeight = Math.ceil(height / 2) * 2;
    const scale = Math.min(box.width / width, box.height / height);
    video.style.objectFit = 'fill';
    video.style.transformOrigin = '0 0';
    // Crop the encoder's one-pixel alignment extension without copying frames.
    // The visible viewport, rather than the coded rectangle, owns centering.
    video.style.transform = `translate(${(box.width - width * scale) / 2}px, ${(box.height - height * scale) / 2}px) scale(${codedWidth * scale / box.width}, ${codedHeight * scale / box.height})`;
    video.style.clipPath = `inset(0 ${(codedWidth - width) * 100 / codedWidth}% ${(codedHeight - height) * 100 / codedHeight}% 0)`;
  }

  public getStatistics(): ILiveBrowserVideoStatistics | undefined {
    return this.statistics ? { ...this.statistics,
      ...(this.lastCaptureTime === undefined ? {} : { frameAgeMs: Math.max(0, performance.now() - this.lastCaptureTime) }),
    } : undefined;
  }

  public getCounters(): { framesReceived: number; framesDecoded: number; framesSkipped: number } {
    const decoded = this.decodedBeforePeer + (this.statistics?.framesDecoded ?? 0);
    const dropped = this.droppedBeforePeer + (this.statistics?.framesDropped ?? 0);
    return { framesReceived: decoded + dropped, framesDecoded: decoded, framesSkipped: dropped };
  }

  public update(stateArg: ILiveBrowserState): void {
    this.geometryObserver.observe(this.options.video);
    const previousSource = this.currentSource;
    const tab = stateArg.tabs.find((tabArg) => tabArg.id === stateArg.activeTabId);
    const candidate: ILiveVideoSource | undefined = document.visibilityState !== 'hidden'
      && stateArg.status === 'running' && tab?.status === 'open'
      ? stateArg.videoSource ? structuredClone(stateArg.videoSource)
        : { tabId: tab.id, generation: tab.generation, viewportRevision: stateArg.viewportRevision,
          viewport: { ...stateArg.viewport } } : undefined;
    if (candidate && (candidate.tabId !== tab?.id || candidate.generation !== tab.generation
      || candidate.viewportRevision !== stateArg.viewportRevision
      || candidate.viewport.width !== stateArg.viewport.width || candidate.viewport.height !== stateArg.viewport.height
      || candidate.viewport.deviceScaleFactor !== stateArg.viewport.deviceScaleFactor)) {
      this.fail('video_connection_failed', new Error('Video source does not match browser state'), this.epoch);
      return;
    }
    const source = candidate && (tab?.streaming || sourceKey(candidate) === this.desiredKey)
      ? candidate : undefined;
    const key = source ? sourceKey(source) : undefined;
    this.currentSource = tab?.streaming ? source : undefined;
    if (key === this.desiredKey) {
      if (!this.currentSource) {
        if (this.frameCallback !== undefined) this.options.video.cancelVideoFrameCallback(this.frameCallback);
        this.frameCallback = undefined;
        this.presentedSourceKey = undefined;
        this.options.onUnavailable();
      } else {
        if (!previousSource || presentationKey(previousSource) !== presentationKey(this.currentSource)) {
          this.presentedSourceKey = undefined;
          this.presentationNotBefore = performance.now();
          this.options.onUnavailable();
        }
        if (this.peer && this.options.video.srcObject) this.waitForPresentation(this.epoch);
      }
      return;
    }
    this.interrupt();
    this.currentSource = tab?.streaming ? source : undefined;
    this.desiredKey = key;
    const epoch = this.epoch;
    const controller = new AbortController();
    this.controller = controller;
    this.enqueue(async () => {
      await this.closeRemote();
      if (!source || epoch !== this.epoch) return;
      try {
        await this.connect(source, controller.signal, epoch);
      } catch (error) {
        if (epoch !== this.epoch || controller.signal.aborted) return;
        this.fail('video_negotiation_failed', error, epoch);
      }
    });
  }

  /** Synchronously prevents old frames or input from surviving a transport/source change. */
  public interrupt(): void {
    this.epoch++;
    this.desiredKey = undefined;
    this.currentSource = undefined;
    this.presentedSourceKey = undefined;
    this.controller?.abort(new Error('Video source was superseded'));
    this.controller = undefined;
    this.options.onUnavailable();
    if (this.frameCallback !== undefined) this.options.video.cancelVideoFrameCallback(this.frameCallback);
    this.frameCallback = undefined;
    clearTimeout(this.connectionTimer);
    clearTimeout(this.sampleTimer);
    clearTimeout(this.senderSampleTimer);
    this.connectionTimer = undefined;
    this.sampleTimer = undefined;
    this.senderSampleTimer = undefined;
    this.framesPresented = 0;
    this.lastPresentedCount = undefined;
    this.lastCaptureTime = undefined;
    this.peer?.close();
    this.peer = undefined;
    this.options.video.pause();
    this.options.video.srcObject = null;
    this.decodedBeforePeer += this.statistics?.framesDecoded ?? 0;
    this.droppedBeforePeer += this.statistics?.framesDropped ?? 0;
    this.statistics = undefined;
  }

  public async stop(): Promise<void> {
    this.interrupt();
    this.geometryObserver.disconnect();
    this.geometrySource = undefined;
    this.contentSize = undefined;
    this.applyGeometry();
    await this.enqueue(() => this.closeRemote());
  }

  private enqueue(operationArg: () => Promise<void>): Promise<void> {
    const operation = this.tail.then(operationArg, operationArg);
    this.tail = operation.catch((error) => {
      this.options.onError({ code: 'renderer_cleanup_failed', message: `Video peer cleanup failed: ${String(error)}`, cause: error });
    });
    return operation;
  }

  private async closeRemote(): Promise<void> {
    if (!this.remoteMayExist) return;
    await this.operation((optionsArg) => this.options.client.closeVideoPeer(optionsArg));
    this.remoteMayExist = false;
  }

  private async connect(sourceArg: ILiveVideoSource, signalArg: AbortSignal, epochArg: number): Promise<void> {
    this.remoteMayExist = true;
    const offer = await this.operation((optionsArg) => this.options.client.openVideoPeer(optionsArg), signalArg);
    if (viewportKey(offer.source) !== viewportKey(sourceArg)) throw new Error('Video offer does not match the current browser source');
    if (sourceArg.frameAlignment !== undefined && presentationKey(offer.source) !== presentationKey(sourceArg)) {
      throw new Error('Native video offer does not match the current presentation fence');
    }
    if (signalArg.aborted || epochArg !== this.epoch) return;
    const peer = new RTCPeerConnection({ iceServers: offer.iceServers, iceTransportPolicy: offer.iceTransportPolicy });
    this.peer = peer;
    this.statistics = { connectionState: 'new', framesDecoded: 0, framesDropped: 0, bytesReceived: 0, bitrate: 0 };
    peer.addEventListener('connectionstatechange', () => {
      if (epochArg !== this.epoch) return;
      if (this.statistics) this.statistics.connectionState = peer.connectionState;
      if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected') {
        this.fail('video_connection_failed', new Error(`Video connection ${peer.connectionState}`), epochArg);
      }
    });
    peer.addEventListener('track', (eventArg) => {
      if (epochArg !== this.epoch || eventArg.track.kind !== 'video') return;
      if ('jitterBufferTarget' in eventArg.receiver) eventArg.receiver.jitterBufferTarget = 0;
      this.options.video.srcObject = new MediaStream([eventArg.track]);
      this.waitForPresentation(epochArg);
      void this.options.video.play().catch((error) => this.fail('video_connection_failed', error, epochArg));
    });
    await this.operation(async ({ signal }) => {
      await peer.setRemoteDescription(offer.description);
      if (signal.aborted) throw signal.reason;
      await peer.setLocalDescription(await peer.createAnswer());
      await this.waitForIce(peer, signal);
    }, signalArg);
    if (signalArg.aborted || epochArg !== this.epoch) return;
    const description = peer.localDescription;
    if (!description?.sdp || description.type !== 'answer' || description.sdp.length > 48 * 1024) {
      throw new Error('Invalid video answer');
    }
    await this.operation((optionsArg) => this.options.client.answerVideoPeer(offer.negotiationId,
      { type: 'answer', sdp: description.sdp }, optionsArg), signalArg);
    if (signalArg.aborted || epochArg !== this.epoch) return;
    if (this.presentedSourceKey === undefined) this.connectionTimer = setTimeout(() => this.fail('video_connection_failed',
      new Error('No video frame received within 30 seconds'), epochArg), 30000);
    void this.sample(peer, signalArg, epochArg);
    void this.sampleSender(signalArg, epochArg);
  }

  private waitForPresentation(epoch: number): void {
    if (this.frameCallback !== undefined) return;
    this.frameCallback = this.options.video.requestVideoFrameCallback((_now, metadata) => {
      this.frameCallback = undefined;
      if (epoch !== this.epoch) return;
      this.framesPresented += this.lastPresentedCount === undefined ? 1
        : Math.max(0, metadata.presentedFrames - this.lastPresentedCount);
      this.lastPresentedCount = metadata.presentedFrames;
      const timing = metadata as VideoFrameCallbackMetadata & { captureTime?: number };
      if (timing.captureTime !== undefined && timing.captureTime <= performance.now()) {
        this.lastCaptureTime = timing.captureTime;
        if (this.statistics) this.statistics.captureToDisplayMs = Math.max(0, metadata.expectedDisplayTime - timing.captureTime);
      }
      this.waitForPresentation(epoch);
      if (!this.currentSource || this.presentedSourceKey === presentationKey(this.currentSource)) return;
      const viewport = this.currentSource.viewport;
      // A resized track keeps its peer, but old/aspect-mismatched frames must
      // never authorize coordinates in the new viewport. Chromium exposes a
      // locally synchronized capture timestamp for received WebRTC frames.
      if (this.currentSource.frameAlignment === 2) {
        const floor = this.currentSource.rtpTimestampFloor;
        if (floor === undefined || metadata.rtpTimestamp === undefined
          || metadata.width !== Math.ceil(viewport.width / 2) * 2
          || metadata.height !== Math.ceil(viewport.height / 2) * 2
          || ((metadata.rtpTimestamp - floor) >>> 0) >= 0x80000000) return;
      } else {
        if (Math.abs(metadata.width * viewport.height - metadata.height * viewport.width)
          > Math.max(viewport.width, viewport.height) * 2) return;
        if (timing.captureTime !== undefined && timing.captureTime < this.presentationNotBefore) return;
      }
      clearTimeout(this.connectionTimer);
      this.connectionTimer = undefined;
      this.presentedSourceKey = presentationKey(this.currentSource);
      this.geometrySource = this.currentSource;
      this.applyGeometry();
      this.options.onPresented(this.currentSource);
    });
  }

  private waitForIce(peerArg: RTCPeerConnection, signalArg: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        peerArg.removeEventListener('icegatheringstatechange', check);
        signalArg.removeEventListener('abort', abort);
      };
      const abort = () => { cleanup(); reject(signalArg.reason); };
      const check = () => {
        if (signalArg.aborted) abort();
        else if (peerArg.iceGatheringState === 'complete') { cleanup(); resolve(); }
      };
      peerArg.addEventListener('icegatheringstatechange', check);
      signalArg.addEventListener('abort', abort, { once: true });
      check();
    });
  }

  private fail(codeArg: 'video_negotiation_failed' | 'video_connection_failed', errorArg: unknown, epochArg: number): void {
    if (epochArg !== this.epoch) return;
    this.interrupt();
    void this.enqueue(() => this.closeRemote()).catch(() => undefined);
    this.options.onError({ code: codeArg, message: String(errorArg), cause: errorArg });
  }

  private async sample(peerArg: RTCPeerConnection, signalArg: AbortSignal, epochArg: number,
    previousArg?: { timestamp: number; bytes: number; delay?: number; emitted: number;
      target?: number; minimum?: number; decoded: number; decode?: number; processing?: number;
      sampledAt: number; presented: number }): Promise<void> {
    let previous = previousArg;
    try {
      const report = await this.operation(() => peerArg.getStats(), signalArg);
      if (epochArg !== this.epoch || !this.statistics) return;
      const sampledAt = performance.now();
      this.statistics.sampledAt = sampledAt;
      this.statistics.framesPresented = this.framesPresented;
      if (previous && sampledAt > previous.sampledAt) this.statistics.presentedFramesPerSecond =
        Math.max(0, (this.framesPresented - previous.presented) * 1000 / (sampledAt - previous.sampledAt));
      report.forEach((entryArg) => {
        if (entryArg.type === 'inbound-rtp' && entryArg.kind === 'video') {
          const interval = previous ? entryArg.timestamp - previous.timestamp : 0;
          this.statistics!.framesDecoded = entryArg.framesDecoded ?? 0;
          this.statistics!.framesDropped = entryArg.framesDropped ?? 0;
          this.statistics!.bytesReceived = entryArg.bytesReceived ?? 0;
          this.statistics!.framesPerSecond = entryArg.framesPerSecond;
          this.statistics!.decoderImplementation = entryArg.decoderImplementation;
          this.statistics!.powerEfficientDecoder = entryArg.powerEfficientDecoder;
          this.statistics!.codec = report.get(entryArg.codecId)?.mimeType;
          this.statistics!.bitrate = interval > 0 ? Math.max(0, (entryArg.bytesReceived - previous!.bytes) * 8000 / interval) : 0;
          const emitted = entryArg.jitterBufferEmittedCount ?? 0;
          const delay = entryArg.jitterBufferDelay;
          const target = entryArg.jitterBufferTargetDelay;
          const minimum = entryArg.jitterBufferMinimumDelay;
          const decoded = entryArg.framesDecoded ?? 0;
          const decode = entryArg.totalDecodeTime;
          const processing = entryArg.totalProcessingDelay;
          if (previous) {
            const emittedCount = emitted - previous.emitted;
            this.statistics!.jitterBufferMs = intervalDelay(delay, previous.delay, emittedCount);
            this.statistics!.jitterBufferTargetMs = intervalDelay(target, previous.target, emittedCount);
            this.statistics!.jitterBufferMinimumMs = intervalDelay(minimum, previous.minimum, emittedCount);
            this.statistics!.decodeMs = intervalDelay(decode, previous.decode, decoded - previous.decoded);
            this.statistics!.processingMs = intervalDelay(processing, previous.processing, decoded - previous.decoded);
          }
          previous = { timestamp: entryArg.timestamp, bytes: entryArg.bytesReceived ?? 0, delay, emitted,
            target, minimum, decoded, decode, processing, sampledAt, presented: this.framesPresented };
        }
        if (entryArg.type === 'transport' && entryArg.selectedCandidatePairId) {
          const pair = report.get(entryArg.selectedCandidatePairId);
          this.statistics!.roundTripMs = pair?.currentRoundTripTime === undefined ? undefined : pair.currentRoundTripTime * 1000;
          this.statistics!.candidateType = report.get(pair?.remoteCandidateId)?.candidateType;
          this.statistics!.transportProtocol = report.get(pair?.remoteCandidateId)?.protocol;
        }
      });
    } catch (error) {
      if (epochArg !== this.epoch || signalArg.aborted) return;
      this.options.onError({ code: 'video_statistics_failed', message: `Could not read video statistics: ${String(error)}`, cause: error });
    }
    if (epochArg === this.epoch && !signalArg.aborted) {
      this.sampleTimer = setTimeout(() => { void this.sample(peerArg, signalArg, epochArg, previous); }, 1000);
    }
  }

  private async sampleSender(signal: AbortSignal, epoch: number): Promise<void> {
    try {
      const sender = await this.operation(options => this.options.client.getVideoStatistics(options), signal);
      if (epoch !== this.epoch || signal.aborted || !this.statistics) return;
      this.statistics.sender = sender;
    } catch (error) {
      if (epoch !== this.epoch || signal.aborted) return;
      this.options.onError({ code: 'video_statistics_failed', message: `Could not read sender video statistics: ${String(error)}`, cause: error });
    }
    if (epoch === this.epoch && !signal.aborted) {
      this.senderSampleTimer = setTimeout(() => { void this.sampleSender(signal, epoch); }, 1000);
    }
  }

  private operation<T>(operationArg: (optionsArg: ILiveBrowserCanvasOperationOptions) => Promise<T>,
    parentSignalArg?: AbortSignal): Promise<T> {
    const controller = new AbortController();
    const abort = () => controller.abort(parentSignalArg?.reason);
    parentSignalArg?.addEventListener('abort', abort, { once: true });
    if (parentSignalArg?.aborted) abort();
    const timer = setTimeout(() => controller.abort(new Error('Video operation timed out')), this.options.operationTimeoutMs);
    return new Promise<T>((resolve, reject) => {
      const onAbort = () => reject(controller.signal.reason);
      controller.signal.addEventListener('abort', onAbort, { once: true });
      if (controller.signal.aborted) onAbort();
      else Promise.resolve().then(() => operationArg({ signal: controller.signal })).then(resolve, reject);
    }).finally(() => {
      clearTimeout(timer);
      parentSignalArg?.removeEventListener('abort', abort);
    });
  }
}
